import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rulesApi } from "../api/rules.api.js";
import { unwrapError } from "../api/client.js";
import { useAuth } from "../auth/useAuth.js";
import { RuleRow } from "../components/rules/RuleRow.js";
import { RuleForm } from "../components/rules/RuleForm.js";
import { ActionButton, Card, EmptyState, ErrorState, Icon, PageHeader, SegmentedTabs, SkeletonList, colors, } from "../components/ui/index.js";
import { useToast } from "../components/ui/ToastProvider.js";
import { useConfirm } from "../components/ui/ConfirmProvider.js";
import { VenueChip } from "../components/ui/VenueChip.js";
import { venueShortName } from "../lib/venue-names.js";
export function RulesPage() {
    const { user } = useAuth();
    const qc = useQueryClient();
    const { showToast } = useToast();
    const { confirm } = useConfirm();
    const [form, setForm] = useState({ kind: "closed" });
    const [groupBy, setGroupBy] = useState("none");
    const q = useQuery({ queryKey: ["rules"], queryFn: rulesApi.list });
    const remove = useMutation({
        mutationFn: (rule) => rulesApi.delete(rule.id),
        onSuccess: (data) => {
            const cancelled = data.cascadedSessions ?? 0;
            const failed = data.failedCancels ?? 0;
            const detail = cancelled > 0
                ? ` Cancelled ${cancelled} planned session${cancelled === 1 ? "" : "s"}${failed > 0 ? ` (${failed} failed)` : ""}.`
                : "";
            showToast(`Preference deleted.${detail}`, failed > 0 ? "error" : "success");
            void qc.invalidateQueries({ queryKey: ["rules"] });
            void qc.invalidateQueries({ queryKey: ["sessions"] });
        },
        onError: async (err) => {
            const apiErr = await unwrapError(err);
            showToast(`Delete failed: ${apiErr.message}`, "error");
        },
    });
    const toggleActive = useMutation({
        mutationFn: (rule) => rule.active ? rulesApi.disable(rule.id) : rulesApi.enable(rule.id),
        onSuccess: (rule) => {
            showToast(`Preference ${rule.active ? "enabled" : "disabled"}`, "success");
            void qc.invalidateQueries({ queryKey: ["rules"] });
        },
        onError: async (err) => {
            const apiErr = await unwrapError(err);
            showToast(`Update failed: ${apiErr.message}`, "error");
        },
    });
    // Resume-with-confirm — when a rule's been paused for >7 days, the next
    // cron tick will generate up to 16 days × occurrences-per-week of new
    // sessions in one go. Warn the user before that surprise.
    const handleToggleActive = async (rule) => {
        if (!rule.active) {
            const lastSeen = rule.lastGeneratedOccurrence ?? rule.updatedAt;
            const ageDays = (Date.now() - Date.parse(lastSeen)) / (24 * 60 * 60 * 1000);
            if (ageDays > 7) {
                const ok = await confirm("Resume this preference?", `It's been paused for ${Math.round(ageDays)} days. Resuming will queue up sessions for every matching day in the next ~2 weeks all at once. Make sure your credentials still have enough credits.`, { confirmText: "Resume" });
                if (!ok)
                    return;
            }
        }
        toggleActive.mutate(rule);
    };
    if (q.isLoading || !user) {
        return _jsx(SkeletonList, { rows: 5 });
    }
    if (q.error) {
        return _jsx(ErrorState, { message: "Failed to load booking preferences" });
    }
    const rules = q.data ?? [];
    const maxRules = user.limits.maxRules;
    const activeCount = rules.filter((r) => r.active).length;
    const atCap = activeCount >= maxRules;
    const handleSaved = () => {
        setForm({ kind: "closed" });
        void qc.invalidateQueries({ queryKey: ["rules"] });
    };
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: "Booking Preferences", subtitle: "Recurring preferences \u2014 turn each one into automatic court bookings.", help: _jsxs(_Fragment, { children: [_jsx("h3", { style: { fontSize: "1.1rem", margin: "0 0 8px" }, children: "What this is" }), _jsxs("p", { style: { margin: "0 0 14px", fontSize: "0.95rem" }, children: ["Your ", _jsx("strong", { children: "recurring booking preferences" }), ". e.g. \"Mon 18:00 at John Orwell, 1 court\". The bot turns these into real Sessions."] }), _jsx("h3", { style: { fontSize: "1.1rem", margin: "0 0 8px" }, children: "The 3 states" }), _jsxs("ul", { style: { margin: 0, paddingLeft: 20, fontSize: "0.95rem" }, children: [_jsxs("li", { style: { marginBottom: 6 }, children: [_jsx("strong", { children: "Active" }), " \u2014 the bot will keep creating future sessions for it."] }), _jsxs("li", { style: { marginBottom: 6 }, children: [_jsx("strong", { children: "Paused" }), " \u2014 no new sessions, but already-booked courts stay."] }), _jsxs("li", { style: { marginBottom: 6 }, children: [_jsx("strong", { children: "Blocked" }), " \u2014 your permissions changed and this preference no longer fits. Edit it (or ask admin) to unblock."] })] }), _jsx("h3", { style: { fontSize: "1.1rem", margin: "16px 0 8px" }, children: "Buttons" }), _jsxs("ul", { style: { margin: 0, paddingLeft: 20, fontSize: "0.95rem" }, children: [_jsxs("li", { style: { marginBottom: 6 }, children: [_jsx("strong", { children: "Edit" }), " \u2014 change day / time / courts."] }), _jsxs("li", { style: { marginBottom: 6 }, children: [_jsx("strong", { children: "Pause / Resume" }), " \u2014 toggle without losing the preference."] }), _jsxs("li", { children: [_jsx("strong", { children: "Delete" }), " \u2014 also cancels any planned future sessions."] })] })] }), children: _jsxs(ActionButton, { onClick: () => setForm({ kind: "create" }), disabled: atCap, children: [_jsx(Icon, { name: "plus", size: 14 }), " Add preference"] }) }), atCap && (_jsxs(Card, { style: {
                    marginBottom: 16,
                    background: colors.warningLight,
                    borderLeft: `4px solid ${colors.warning}`,
                }, children: ["You have reached your maximum of ", maxRules, " active booking preference", maxRules === 1 ? "" : "s", ". Disable an existing preference to create another."] })), rules.length === 0 ? (_jsx(EmptyState, { message: "No booking preferences yet. Create one to start auto-booking sessions on a recurring schedule.", action: _jsxs(ActionButton, { onClick: () => setForm({ kind: "create" }), disabled: atCap, children: [_jsx(Icon, { name: "plus", size: 14 }), " Add preference"] }) })) : (_jsxs(_Fragment, { children: [_jsx("div", { style: { marginBottom: 16 }, children: _jsx(SegmentedTabs, { ariaLabel: "Group preferences by", value: groupBy, onChange: setGroupBy, tabs: [
                                { key: "none", label: "Status" },
                                { key: "day", label: "By day" },
                                { key: "venue", label: "By venue" },
                            ] }) }), _jsx(GroupedRules, { rules: rules, groupBy: groupBy, onEdit: (r) => setForm({ kind: "edit", rule: r }), onToggleActive: (r) => void handleToggleActive(r), onDelete: (r) => remove.mutate(r) })] })), form.kind !== "closed" && (_jsx(RuleForm, { mode: form.kind, initial: form.kind === "edit" ? form.rule : undefined, permissions: user.permissions, onClose: () => setForm({ kind: "closed" }), onSaved: handleSaved }))] }));
}
const DAY_NAMES = [
    "Sundays",
    "Mondays",
    "Tuesdays",
    "Wednesdays",
    "Thursdays",
    "Fridays",
    "Saturdays",
];
/**
 * Section the rules list by the chosen grouping:
 *  - "none"  → by STATUS (Active → Blocked → Paused), the default view
 *  - "day"   → by day of week (Mon → Sun)
 *  - "venue" → by venue (canonical colour via VenueChip)
 * Within every section, sort by Mon → Sun then start-time.
 */
function GroupedRules({ rules, groupBy, onEdit, onToggleActive, onDelete, }) {
    const sections = useMemo(() => {
        const monFirst = (d) => (d === 0 ? 6 : d - 1);
        const byDayTime = (a, b) => monFirst(a.dayOfWeek) - monFirst(b.dayOfWeek) ||
            a.startTime.localeCompare(b.startTime);
        const dot = (color, label) => (_jsxs(_Fragment, { children: [_jsx("span", { "aria-hidden": true, style: { width: 8, height: 8, borderRadius: 999, background: color } }), _jsx("span", { style: { color }, children: label })] }));
        if (groupBy === "none") {
            const statusKey = (r) => !r.active ? "paused" : r.eligibility && !r.eligibility.ok ? "blocked" : "active";
            const buckets = {
                active: [],
                blocked: [],
                paused: [],
            };
            for (const r of rules)
                buckets[statusKey(r)].push(r);
            const meta = [
                { key: "active", label: "Active", color: colors.success },
                { key: "blocked", label: "Blocked", color: colors.danger },
                { key: "paused", label: "Paused", color: colors.textTertiary },
            ];
            return meta
                .filter((m) => buckets[m.key].length > 0)
                .map((m) => ({
                id: m.key,
                header: dot(m.color, m.label),
                rules: [...buckets[m.key]].sort(byDayTime),
            }));
        }
        if (groupBy === "day") {
            const byDay = new Map();
            for (const r of rules) {
                const list = byDay.get(r.dayOfWeek) ?? [];
                list.push(r);
                byDay.set(r.dayOfWeek, list);
            }
            return [1, 2, 3, 4, 5, 6, 0]
                .filter((d) => byDay.has(d))
                .map((d) => ({
                id: `day-${d}`,
                header: dot(colors.textSecondary, DAY_NAMES[d] ?? `Day ${d}`),
                rules: [...(byDay.get(d) ?? [])].sort((a, b) => a.startTime.localeCompare(b.startTime)),
            }));
        }
        // venue
        const byVenue = new Map();
        for (const r of rules) {
            const list = byVenue.get(r.venueId) ?? [];
            list.push(r);
            byVenue.set(r.venueId, list);
        }
        return [...byVenue.keys()]
            .sort((a, b) => venueShortName(a).localeCompare(venueShortName(b)))
            .map((venueId) => ({
            id: `venue-${venueId}`,
            header: _jsx(VenueChip, { venueId: venueId }),
            rules: [...(byVenue.get(venueId) ?? [])].sort(byDayTime),
        }));
    }, [rules, groupBy]);
    return (_jsx("div", { style: { display: "grid", gap: 22 }, children: sections.map((s) => (_jsxs("div", { children: [_jsxs("div", { style: {
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 8,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                    }, children: [s.header, _jsxs("span", { style: { color: colors.textTertiary }, children: ["\u00B7 ", s.rules.length] })] }), _jsx("div", { style: { display: "grid", gap: 10 }, children: s.rules.map((rule) => (_jsx(RuleRow, { rule: rule, onEdit: onEdit, onToggleActive: onToggleActive, onDelete: onDelete }, rule.id))) })] }, s.id))) }));
}
